import { useState, Key, useEffect, ReactNode, ChangeEvent } from 'react';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import SearchBar from '@/components/form/SearchBar';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface BaseTransferListProps<T> {
    leftList: T[];
    rightList: T[];
    idKey: keyof T;
    labelKey: keyof T;
    isLoading?: boolean;
    listsTitle: { left: string; right: string };
    listHeight?: number;
    clustersAutoCompleteNode?: ReactNode | null;
    onUpdateLists: (newLeft: T[], newRight: T[]) => void;
}

export default function BaseTransferList<T>({
    leftList,
    rightList,
    idKey,
    labelKey,
    isLoading = false,
    listsTitle,
    listHeight = 200,
    clustersAutoCompleteNode = null,
    onUpdateLists,
}: BaseTransferListProps<T>) {
    const { t } = useTranslation('common');
    const [checked, setChecked] = useState<readonly T[]>([]);

    const [left, setLeft] = useState<T[]>(leftList);
    const [right, setRight] = useState<T[]>(rightList);
    const [originalLeft, setOriginalLeft] = useState<T[]>(leftList);
    const [originalRight, setOriginalRight] = useState<T[]>(rightList);

    const [leftSearch, setLeftSearch] = useState('');
    const [rightSearch, setRightSearch] = useState('');

    useEffect(() => {
        setLeft(leftList);
        setOriginalLeft(leftList);
        setLeftSearch('');
        setChecked((c) => c.filter((item) => leftList.includes(item)));
    }, [leftList]);

    useEffect(() => {
        setRight(rightList);
        setOriginalRight(rightList);
        setRightSearch('');
        setChecked((c) => c.filter((item) => rightList.includes(item)));
    }, [rightList]);

    // Helpers
    function not(a: readonly T[], b: readonly T[]) {
        return a.filter((value) => !b.includes(value));
    }
    function intersection(a: readonly T[], b: readonly T[]) {
        return a.filter((value) => b.includes(value));
    }

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const handleToggle = (value: T) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];
        if (currentIndex === -1) newChecked.push(value);
        else newChecked.splice(currentIndex, 1);
        setChecked(newChecked);
    };

    const handleCheckedRight = () => {
        const newRight = right.concat(leftChecked);
        const newLeft = not(originalLeft, leftChecked);
        setRight(newRight);
        setLeft(newLeft);
        setOriginalLeft(not(originalLeft, leftChecked));
        setOriginalRight(originalRight.concat(leftChecked));
        setChecked(not(checked, leftChecked));
        onUpdateLists(newLeft, newRight);
    };

    const handleCheckedLeft = () => {
        const newLeft = left.concat(rightChecked);
        const newRight = not(originalRight, rightChecked);
        setLeft(newLeft);
        setRight(newRight);
        setOriginalLeft(originalLeft.concat(rightChecked));
        setOriginalRight(not(originalRight, rightChecked));
        setChecked(not(checked, rightChecked));
        onUpdateLists(newLeft, newRight);
    };

    const searchHandler = (side: 'left' | 'right', value: string) => {
        if (side === 'left') {
            setLeft(
                originalLeft.filter((item) =>
                    (item[labelKey] as unknown as string).toLowerCase().includes(value.toLowerCase()),
                ),
            );
        } else {
            setRight(
                originalRight.filter((item) =>
                    (item[labelKey] as unknown as string).toLowerCase().includes(value.toLowerCase()),
                ),
            );
        }
    };

    const customList = (title: ReactNode, side: 'left' | 'right', items: T[]) => (
        <Card
            sx={{
                width: '100%',
                maxWidth: 300,
                mx: 'auto',
                height: listHeight,
                minHeight: '200px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ px: 1, py: 0.4, fontSize: 16, fontWeight: 'medium' }}>{title}</Box>
            <Box sx={{ px: 1, mb: 1, visibility: side === 'left' ? 'visible' : 'hidden' }}>
                {clustersAutoCompleteNode}
            </Box>

            <SearchBar
                size="small"
                placeholder={
                    side === 'left'
                        ? t('Search {{name}}...', { name: listsTitle.left })
                        : t('Search {{name}}...', { name: listsTitle.right })
                }
                value={side === 'left' ? leftSearch : rightSearch}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    const val = e.target.value;
                    if (side === 'left') setLeftSearch(val);
                    else setRightSearch(val);
                    searchHandler(side, val);
                }}
                sx={{ height: 40 }}
            />
            <Divider />
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List dense role="list">
                        {items.map((value) => {
                            const labelId = `transfer-list-item-${value[idKey]}-label`;
                            return (
                                <ListItemButton
                                    key={value[idKey] as Key}
                                    role="listitem"
                                    onClick={handleToggle(value)}
                                    sx={{ p: 0, height: 30 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <Checkbox
                                            checked={checked.includes(value)}
                                            tabIndex={-1}
                                            inputProps={{ 'aria-labelledby': labelId }}
                                            sx={{ p: 0 }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText id={labelId} primary={`${value[labelKey]}`} />
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}
            </Box>
        </Card>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                mt: 1,
            }}
        >
            <Box
                sx={{
                    flex: '1 1 320px',
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: 300,
                    // mx: 'auto',
                }}
            >
                {customList(listsTitle.left, 'left', left)}
            </Box>

            <Box
                sx={{
                    flex: '0 0 auto',
                    width: { xs: '100%', sm: 'auto' },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'column' },
                        gap: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label={t('Move selected left')}
                        sx={{
                            minWidth: 80,
                        }}
                    >
                        &lt;
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label={t('Move selected right')}
                        sx={{
                            minWidth: 80,
                        }}
                    >
                        &gt;
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    flex: '1 1 320px',
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: 300,
                    // mx: 'auto',
                }}
            >
                {customList(listsTitle.right, 'right', right)}
            </Box>
        </Box>
    );
}
